import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Heart,
  X,
  Minimize2,
  Maximize2,
  ExternalLink,
  Trash2,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_KEY = 'tla_demo_favorites';

const SIZES = {
  minimized: { width: '48px', height: '48px', showList: false, maxItems: 0 },
  compact:   { width: '200px', height: '60px',  showList: false, maxItems: 0 },
  small:     { width: '280px', height: '200px', showList: true,  maxItems: 2 },
  medium:    { width: '320px', height: '320px', showList: true,  maxItems: 4 },
  large:     { width: '360px', height: '480px', showList: true,  maxItems: 8 },
};

function readDemoFavorites() {
  try {
    return JSON.parse(localStorage.getItem(DEMO_KEY) || '[]');
  } catch {
    return [];
  }
}

export default function FloatingFavorites() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [backendFavorites, setBackendFavorites] = useState([]);
  const [demoFavorites, setDemoFavorites] = useState(readDemoFavorites);
  const [size, setSize] = useState('compact');
  const [loading, setLoading] = useState(false);

  const favorites = [...backendFavorites, ...demoFavorites];

  // Load backend favorites when user changes
  useEffect(() => {
    if (user) {
      fetchBackendFavorites();
    } else {
      setBackendFavorites([]);
    }
  }, [user]);

  // Refresh on favorites-updated (FavoriteButton dispatches this)
  useEffect(() => {
    const handleUpdate = () => {
      setDemoFavorites(readDemoFavorites());
      if (user) fetchBackendFavorites();
    };
    window.addEventListener('favorites-updated', handleUpdate);
    return () => window.removeEventListener('favorites-updated', handleUpdate);
  }, [user]);

  const fetchBackendFavorites = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const raw = Array.isArray(data) ? data : (data.favorites || []);
        setBackendFavorites(raw.map(f => ({
          favorite_id: f.favorite_id || f.id,
          listing_id: f.listing?.id || f.listing_id,
          listing_title: f.listing?.title || f.listing_title,
          listing_photo: f.listing?.hero_photo_url || f.listing_photo,
          listing_location: f.listing?.location_name || f.listing_location,
          listing_price: f.listing?.base_amount || f.listing_price,
          created_at: f.created_at,
          is_demo: false,
        })));
      }
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (fav, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fav.is_demo) {
      try {
        const updated = readDemoFavorites().filter(f => f.listing_id !== fav.listing_id);
        localStorage.setItem(DEMO_KEY, JSON.stringify(updated));
        setDemoFavorites(updated);
        window.dispatchEvent(new Event('favorites-updated'));
      } catch {
        console.error('Failed to remove demo favorite');
      }
      return;
    }
    try {
      await fetch(`${API_URL}/api/favorites/${fav.listing_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBackendFavorites(prev => prev.filter(f => f.listing_id !== fav.listing_id));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  const cycleSize = (direction) => {
    const sizeOrder = ['minimized', 'compact', 'small', 'medium', 'large'];
    const currentIndex = sizeOrder.indexOf(size);
    const newIndex = direction === 'up'
      ? Math.min(currentIndex + 1, sizeOrder.length - 1)
      : Math.max(currentIndex - 1, 0);
    setSize(sizeOrder[newIndex]);
  };

  // Show if logged in OR if there are demo favorites
  if (!user && demoFavorites.length === 0) return null;

  const currentSize = SIZES[size];
  const displayFavorites = favorites.slice(0, currentSize.maxItems);

  if (size === 'minimized') {
    return (
      <div className="fixed bottom-6 right-6 z-40" data-testid="floating-favorites">
        <button
          onClick={() => setSize('compact')}
          className="w-12 h-12 bg-white border border-slate-700 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-800 transition-colors"
        >
          <Heart className={`w-5 h-5 ${favorites.length > 0 ? 'text-red-500 fill-current' : 'text-slate-600'}`} />
        </button>
        {favorites.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold pointer-events-none">
            {favorites.length > 9 ? '9+' : favorites.length}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-40 transition-all duration-300 ease-in-out"
      style={{ width: currentSize.width, maxHeight: currentSize.height }}
      data-testid="floating-favorites"
    >
      <div className="bg-white border border-slate-700 rounded-lg shadow-xl overflow-hidden flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-slate-800/50">
          <div className="flex items-center gap-2">
            <Heart className={`w-4 h-4 ${favorites.length > 0 ? 'text-red-500 fill-current' : 'text-slate-600'}`} />
            <span className="text-sm font-medium text-slate-200">
              {t('favorites.saved_label', 'Saved')} ({favorites.length})
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => cycleSize('down')}
              className="p-1 text-slate-600 hover:text-slate-200 transition-colors"
              title={t('favorites.smaller', 'Smaller')}
            >
              <Minimize2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => cycleSize('up')}
              className="p-1 text-slate-600 hover:text-slate-200 transition-colors"
              title={t('favorites.larger', 'Larger')}
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setSize('minimized')}
              className="p-1 text-slate-600 hover:text-slate-200 transition-colors"
              title={t('favorites.minimize', 'Minimize')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Content */}
        {currentSize.showList ? (
          <div className="flex-1 overflow-y-auto">
            {favorites.length === 0 ? (
              <div className="p-4 text-center">
                <Heart className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-800">{t('favorites.no_saved', 'No saved properties yet')}</p>
                <Link to="/listings" className="text-xs text-signal-blue hover:underline mt-1 inline-block">
                  {t('favorites.browse', 'Browse properties')}
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {displayFavorites.map((fav) => (
                  <Link
                    key={fav.favorite_id || fav.listing_id}
                    to={`/listings/${fav.listing_id}`}
                    className="flex items-center gap-3 p-2 hover:bg-slate-800/50 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-slate-800">
                      {fav.listing_photo ? (
                        <img
                          src={fav.listing_photo}
                          alt={fav.listing_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Heart className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm text-slate-200 truncate font-medium">{fav.listing_title}</p>
                        {fav.is_demo && (
                          <span className="flex-shrink-0 text-[9px] uppercase tracking-wider font-bold text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded">
                            {t('favorites.preview_badge', 'Preview')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{fav.listing_location}</span>
                      </div>
                      {fav.listing_price > 0 && (
                        <p className="text-xs text-military-gold font-medium">
                          €{fav.listing_price.toLocaleString()}/mo
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => removeFavorite(fav, e)}
                      className="p-1.5 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Link>
                ))}
              </div>
            )}
            {favorites.length > currentSize.maxItems && (
              <div className="p-2 border-t border-slate-800 text-center">
                <Link
                  to="/favorites"
                  className="text-xs text-signal-blue hover:underline flex items-center justify-center gap-1"
                >
                  {t('favorites.view_all_pre', 'View all')} {favorites.length} {t('favorites.view_all_post', 'saved')}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            )}
          </div>
        ) : (
          <Link
            to="/favorites"
            className="flex items-center justify-between px-3 py-2 hover:bg-slate-800/50 transition-colors"
          >
            <span className="text-sm text-slate-300">
              {favorites.length} {t('favorites.properties_saved', 'saved properties')}
            </span>
            <ExternalLink className="w-4 h-4 text-slate-600" />
          </Link>
        )}
      </div>
    </div>
  );
}
